/**
 * Step 4 - Parametry planu + Generowanie
 * Długość, start, częstotliwość, miks typów
 * Primary CTA: "Generuj plan"
 * 
 * PREMIUM VERSION V2 - Consistent with wizard style, better guidance
 */
import React from 'react';
import { Calendar, Clock, Zap, Layers, Check, Sparkles } from 'lucide-react';

const DURATION_OPTIONS = [
  { value: 7, label: '7 dni', desc: 'Tydzień próbny' },
  { value: 14, label: '14 dni', desc: '2 tygodnie' },
  { value: 30, label: '30 dni', desc: 'Pełny miesiąc' }
];

const START_OPTIONS = [
  { value: 'today', label: 'Dziś' },
  { value: 'tomorrow', label: 'Jutro' },
  { value: 'nextWeek', label: 'Za tydzień' }
];

const FREQUENCY_OPTIONS = [
  { value: 3, label: '3 posty', desc: 'tygodniowo' },
  { value: 5, label: '5 postów', desc: 'tygodniowo', recommended: true },
  { value: 7, label: '7 postów', desc: 'tygodniowo' }
];

export default function StepGenerate({ 
  value = {}, 
  onChange, 
  onGenerate,
  generating = false,
  generated = false
}) {
  const duration = value.duration || 7;
  const startOption = value.startOption || 'today';
  const frequency = value.frequency || 5;
  const mixedTypes = value.mixedTypes !== false;
  
  const updateField = (field, val) => {
    onChange({ ...value, [field]: val });
  };
  
  // Calculate estimated posts
  const weeksCount = Math.ceil(duration / 7);
  const estimatedPosts = weeksCount * frequency;
  
  return (
    <div className="space-y-8">
      {/* HEADER */}
      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
          Ustaw parametry planu
        </h1>
        <p className="text-base text-gray-600">
          Wybierz jak długo ma trwać plan i jak często publikować.
        </p>
      </div>
      
      {/* DURATION CARD */}
      <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-md hover:shadow-lg transition-all">
        <div className="flex items-center gap-4 mb-5">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 shadow-md shadow-orange-300/30 flex items-center justify-center">
            <Calendar className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Długość planu</h2>
            <p className="text-sm text-gray-500">Na ile dni wygenerować treści?</p>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-3">
          {DURATION_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => updateField('duration', opt.value)}
              className={`
                p-4 rounded-xl border-2 text-center transition-all
                ${duration === opt.value 
                  ? 'border-orange-500 bg-orange-50 shadow-md' 
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }
              `}
            >
              <div className={`text-xl font-bold ${duration === opt.value ? 'text-orange-700' : 'text-gray-900'}`}>
                {opt.label}
              </div>
              <div className="text-sm text-gray-500 mt-0.5">{opt.desc}</div>
            </button>
          ))}
        </div>
      </div>
      
      {/* START DATE CARD */}
      <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-md hover:shadow-lg transition-all">
        <div className="flex items-center gap-4 mb-5">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 shadow-md shadow-orange-300/30 flex items-center justify-center">
            <Clock className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Kiedy startujemy?</h2>
            <p className="text-sm text-gray-500">Od kiedy zacząć publikowanie</p>
          </div>
        </div>
        
        <div className="flex gap-3">
          {START_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => updateField('startOption', opt.value)}
              className={`
                flex-1 px-4 py-3 rounded-xl border-2 font-semibold transition-all
                ${startOption === opt.value 
                  ? 'border-orange-500 bg-orange-50 text-orange-700 shadow-md' 
                  : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                }
              `}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* FREQUENCY CARD */}
      <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-md hover:shadow-lg transition-all">
        <div className="flex items-center gap-4 mb-5">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 shadow-md shadow-orange-300/30 flex items-center justify-center">
            <Zap className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Ile postów tygodniowo?</h2>
            <p className="text-sm text-gray-500">Częstotliwość publikacji</p>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-3">
          {FREQUENCY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => updateField('frequency', opt.value)}
              className={`
                p-4 rounded-xl border-2 text-center transition-all relative
                ${frequency === opt.value 
                  ? 'border-orange-500 bg-orange-50 shadow-md' 
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }
              `}
            >
              {opt.recommended && (
                <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-amber-500 text-white text-xs font-bold rounded-full">
                  Zalecane
                </span>
              )}
              <div className={`text-xl font-bold ${frequency === opt.value ? 'text-orange-700' : 'text-gray-900'}`}>
                {opt.label}
              </div>
              <div className="text-sm text-gray-500 mt-0.5">{opt.desc}</div>
            </button>
          ))}
        </div>
      </div>
      
      {/* MIXED TYPES TOGGLE */}
      <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 shadow-md hover:shadow-lg transition-all">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 shadow-md shadow-orange-300/30 flex items-center justify-center">
              <Layers className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Różne typy postów</h2>
              <p className="text-sm text-gray-500">
                {mixedTypes ? 'Włączone — oferta, edukacja, kulisy, opinie' : 'Wyłączone — jednolity typ treści'}
              </p>
            </div>
          </div>
          
          <button
            type="button"
            onClick={() => updateField('mixedTypes', !mixedTypes)}
            className={`
              relative w-14 h-8 rounded-full transition-colors flex-shrink-0
              ${mixedTypes ? 'bg-orange-600' : 'bg-gray-300'}
            `}
          >
            <div className={`
              absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform
              ${mixedTypes ? 'left-7' : 'left-1'}
            `}>
              {mixedTypes && (
                <Check className="w-4 h-4 text-orange-600 absolute top-1 left-1" />
              )}
            </div>
          </button>
        </div>
      </div>
      
      {/* SUMMARY BOX - What AI will generate */}
      <div className="bg-gradient-to-br from-orange-50 via-purple-50/80 to-orange-50 rounded-2xl p-5 border border-orange-200/60">
        <div className="flex items-center gap-3 mb-3">
          <Sparkles className="w-5 h-5 text-orange-600" />
          <h3 className="font-bold text-orange-800">Co wygeneruje AI?</h3>
        </div>
        <ul className="space-y-2 text-sm text-orange-700">
          <li className="flex items-center gap-2">
            <Check className="w-4 h-4 text-orange-500" />
            <span><strong>{estimatedPosts} gotowych postów</strong> do publikacji</span>
          </li>
          <li className="flex items-center gap-2">
            <Check className="w-4 h-4 text-orange-500" />
            <span>Treści dopasowane do Twojej oferty i klientów</span>
          </li>
          <li className="flex items-center gap-2">
            <Check className="w-4 h-4 text-orange-500" />
            <span>Harmonogram publikacji na {duration} dni</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
